'use client';

import { useMemo } from 'react';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { ArrowRight, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { IndustryCategoryItem } from '@/lib/backend-api';
import { generateCategoryInternalPath } from '@/lib/industry-utils';
import { getCategoryLabel } from '@/lib/industry-categories';
import { useLocale } from '@/hooks/use-locale';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Container } from '@/components/common/container';
import { LocaleLink } from '@/components/locale-link';

interface IndustryPageClientProps {
  categories: IndustryCategoryItem[];
}

export default function IndustryPageClient({
  categories,
}: IndustryPageClientProps) {
  const { t, i18n } = useTranslation();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);
  const { locale } = useLocale();

  const topLevels = useMemo(
    () =>
      categories
        .filter((c) => c.parent === null)
        .sort((a, b) => b.total_revenue - a.total_revenue),
    [categories],
  );

  return (
    <Container className="py-12 flex flex-col gap-10">
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-semibold tracking-tight text-foreground">
          {t('pages.industry.title')}
        </h1>
        <div className="text-muted-foreground text-lg max-w-2xl font-light">
          <p className="inline">{t('pages.industry.description')} </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 text-muted-foreground/60 hover:text-muted-foreground cursor-help inline align-middle" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="text-xs">
                {locale === 'de'
                  ? 'Firmen werden ihrer Branche über den primären NACE-Code zugeordnet.'
                  : 'Companies are grouped by their primary NACE code.'}
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="text-sm text-muted-foreground/70">
          {locale === 'de'
            ? 'Klicken Sie auf eine Branche für mehr Details'
            : 'Click a branch for more details'}
        </p>
      </div>

      <div className="flex flex-col gap-2 w-full">
        {topLevels.map((node) => (
          <LocaleLink
            key={node.category}
            href={generateCategoryInternalPath(node.category, locale)}
            className="group relative flex items-center py-2 px-4 rounded-xl hover:bg-muted/30 transition-all duration-300 border border-black/5 bg-card/40 backdrop-blur-sm"
          >
            <div className="flex-grow min-w-0 mr-6 overflow-hidden">
              <h2 className="text-sm font-medium text-foreground line-clamp-2 min-w-0 group-hover:text-primary transition-colors">
                {getCategoryLabel(node.category, locale)}
              </h2>
              <div className="lg:hidden flex items-center gap-2 mt-0.5">
                <p className="text-[11px] font-medium text-foreground/70">
                  {fmt.compactCurrency.format(node.total_revenue)}
                </p>
                <span className="text-muted-foreground/40 text-[11px]">·</span>
                <p
                  className={cn(
                    'text-[11px] font-medium',
                    node.total_profit >= 0
                      ? 'text-green-700 dark:text-green-500'
                      : 'text-red-700 dark:text-red-500',
                  )}
                >
                  {fmt.compactCurrency.format(node.total_profit)}
                </p>
                <span className="text-muted-foreground/40 text-[11px]">·</span>
                <p className="text-[11px] font-medium text-muted-foreground">
                  {node.company_count.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="hidden lg:flex flex-shrink-0 items-center gap-0 pr-4">
              <div className="w-[100px] flex flex-col text-right">
                <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider mb-0.5">
                  {t('common.labels.revenue')}
                </p>
                <p className="text-sm font-medium tracking-tight text-foreground truncate">
                  {fmt.compactCurrency.format(node.total_revenue)}
                </p>
              </div>
              <div className="w-[100px] flex flex-col text-right">
                <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider mb-0.5">
                  {t('common.labels.profit')}
                </p>
                <p
                  className={cn(
                    'text-sm font-medium tracking-tight truncate',
                    node.total_profit >= 0
                      ? 'text-green-700 dark:text-green-500'
                      : 'text-red-700 dark:text-red-500',
                  )}
                >
                  {fmt.compactCurrency.format(node.total_profit)}
                </p>
              </div>
              <div className="w-[80px] flex flex-col text-right">
                <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider mb-0.5">
                  {t('common.labels.companies')}
                </p>
                <p className="text-sm font-medium tracking-tight text-foreground/70 dark:text-zinc-300 truncate">
                  {node.company_count.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 size-7 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
              <ArrowRight size={12} />
            </div>
          </LocaleLink>
        ))}
      </div>
    </Container>
  );
}
